import {BaseComponent} from "../../base.component";
import {LoggerService} from "../../common/logger.service";
import {UserService} from "../../common/user.service";
import {TranslateService} from "../../translation/translate.service";
import {AnalyticsService} from "../../analytics/analytics.service";
import {
    Component,
    computed,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    Signal,
    SimpleChanges
} from "@angular/core";
import {Router} from "@angular/router";
import {IProduct, IProductGroup} from "../product-models";
import {from} from "rxjs";
import {Constants} from "../../constants";
import {CartUtil} from "../../cart/cart-util";
import {IShopUser} from "../../session/customer-models";
import {IShareLinkOptions} from "../../social/social-models";
import {ProductCacheService} from "../product-cache.service";
import {take} from "rxjs/operators";
import {UsanaCurrencyPipe} from "../../currency/usana-currency.pipe";
import {AutoOrder} from "../../autoorder/auto-order-models";
import {AutoOrderCacheService} from "../../ao-cart/ao-cache.service";
import {toSignal} from "@angular/core/rxjs-interop";

@Component({
    selector: 'product-buy-box',
    templateUrl: './product-buy-box.component.html',
    styleUrls: ['./product-buy-box.component.scss'],
    standalone: false
})
export class ProductBuyBoxComponent extends BaseComponent implements OnInit, OnChanges {

    @Input() productGroup: IProductGroup;
    @Input() product: IProduct;
    @Input() productQty: number;
    @Input() isPdp: boolean = true;
    @Input() onUpdateProduct?: (product: IProduct) => void;
    @Output() readonly productAddedEvent = new EventEmitter();

    showAddToAo: boolean;
    showAddToCart: boolean;
    subscribeAndSaveMsg: string;
    quantity: number = 1;
    displayUrlShareables: boolean = false;
    shareOptions: IShareLinkOptions;
    importTaxDisplay: string;

    validAutoOrders: Signal<AutoOrder[]> = toSignal(this.aoCacheService.validAutoOrdersSubject.watch(), {initialValue: []});
    hasAutoOrders: Signal<boolean> = computed(() => this.validAutoOrders()?.length > 0);
    aoStrikethroughPrice = computed(() => {
        if (this.product && this.showAoStrikethroughByProduct(this.product)) {
            if (this.hasAutoOrders()) {
                return this.product.price.price;
            } else if (this.showRetailStrikethroughByProduct(this.product)) {
                return this.product.retailPrice.price;
            }
        }
        return null;
    });

    constructor(loggerService: LoggerService,
                userService: UserService,
                private readonly translateService: TranslateService,
                private readonly analyticsService: AnalyticsService,
                private readonly router: Router,
                private readonly currencyPipe: UsanaCurrencyPipe,
                private readonly aoCacheService: AutoOrderCacheService,
                private readonly productCacheService: ProductCacheService
    ) {
        super(userService, loggerService);
    }

    ngOnInit() {
        this.init();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.product && this.productChanged(!changes.product.firstChange, changes.product.previousValue, changes.product.currentValue)) {
            this.init();
        }
    }

    get selectedProductOption(): string {
        return this.isPdp ? this.productCacheService.buyBoxSingleSelectedOption :
            this.productCacheService.getBuyBoxMultipleSelectedOption(this.product?.usanaId);
    }

    set selectedProductOption(value: string) {
        if (this.isPdp) {
            this.productCacheService.buyBoxSingleSelectedOption = value;
        } else {
            this.productCacheService.setBuyBoxMultipleSelectedOption(this.product?.usanaId, value);
        }
    }

    get isAoSelected(): boolean {
        return this.selectedProductOption == 'addToAo' && this.showAddToAo;
    }

    get isCartSelected(): boolean {
        return this.selectedProductOption == 'addToCart' && this.showAddToCart;
    }

    init() {
        if (this.product) {
            if (this.productQty) {
                this.quantity = this.productQty;
            }
            this.shareOptions = {shareType: Constants.SHARE_TYPE_SHARE_ONE, shareId: this.product.usanaId, shareFrom: Constants.PRODUCT_STATE};

            from(this.getCustomerData()).subscribe((user: IShopUser) => {
                this.showAddToAo = CartUtil.showAddToAoButtonProduct(this.product) && (this.canEditAutoOrderOnline || this.canAddAutoOrderOnline) && this.isPdp;
                this.showAddToCart = CartUtil.showAddToCartButtonProduct(this.product, user) && this.showCart;
                this.initSelectedProductOption();

                this.displayUrlShareables = this.showUrlShareables && this.showAddToCart && !!this.product?.retailPrice;
                this.setImportTaxDisplay(String(this.product.price.vatTaxRate));
                this.setSubscribeAndSaveMsg();
            });
        }
    }

    productChanged(productUpdated: boolean, previousProduct: IProduct, currentProduct: IProduct) {
        return productUpdated && (currentProduct?.id !== previousProduct?.id || currentProduct?.usanaId !== previousProduct?.usanaId);
    }

    initSelectedProductOption() {
        if (!(this.selectedProductOption && (this.isAoSelected || this.isCartSelected))) {
            if (this.showAddToAo) {
                this.selectedProductOption = 'addToAo';
            } else {
                this.selectedProductOption = this.showAddToCart ? 'addToCart' : null;
            }
        }
    }

    setSubscribeAndSaveMsg() {
        if (this.showAddToAo) {
            this.currencyPipe.transform(this.product.aoPrice.price)
                .pipe(take(1))
                .subscribe((formattedPrice: string) => {
                    this.translateService.translateRequest({
                        tag: 'cart.product.subscribe.save',
                        replacements: new Map([['price', formattedPrice]])
                    }).then((translation: string) => {
                        this.subscribeAndSaveMsg = translation;
                    });
                });
        }
    }

    sendUpsellPromotionClick(e) {
        this.analyticsService.upsellPromotionClickEvent( this.showPcComparison, window.location.pathname, e.target.className);
    }

    onProductAdded() {
        this.productAddedEvent.emit();
    }

    updateProductEvent(product: IProduct) {
        if (this.onUpdateProduct) {
            this.onUpdateProduct(product);
        } else {
            this.goToProduct(product);
        }
    }

    goToProduct(product: IProduct) {
        this.router.navigate([Constants.PRODUCT_STATE, product.usanaId]);
    }

    setImportTaxDisplay(taxrate: string) {
        if (this.shouldDisplayImportTax) {
            this.translateService.translateRequest({
                tag: 'cart.display.import.tax',
                replacements: new Map([[Constants.TAX_RATE_KEY, taxrate]])
            }).then((translation: string) => {
                this.importTaxDisplay = translation;
            });
        }
    }

}
